import React, { ReactNode } from 'react';
import { Resources } from '@erecruit/types';
import { Intl } from '@erecruit/core';
import { DynamicLabels } from '@erecruit/modules';
import { Modal, H1, H3, styled } from '@erecruit/ui-kit';
import { ModalSpinner } from './CredentialSpinners';

const { FormattedMessage } = Intl;

interface DetailsContentProps {
  label: keyof Resources.RequirementDetails;
  value:
    | Resources.RequirementDetails[keyof Resources.RequirementDetails]
    | Resources.MatchRequirementProgress['expiresOn'];
}

interface ModalTitle {
  title: ReactNode;
  subtitle: Resources.MatchRequirementProgress['name'];
}

export const ModalTitle = ({ title, subtitle }: ModalTitle) => (
  <React.Fragment>
    <H1 color="headerTertiary" align="center">
      {title}
    </H1>
    <H3 align="center">{subtitle}</H3>
  </React.Fragment>
);

type CredentialDetailsProps = {
  isDetailsVisible: boolean;
  setDetailsVisibility: Function;
  subtitle: Resources.MatchRequirementProgress['name'];
  isLoadingDetails: boolean;
  details: [
    Resources.RequirementDetails &
      Pick<Resources.MatchRequirementProgress, 'expiresOn'>,
  ];
};

const DetailsModal = styled(Modal)({
  '.ant-modal-header': {
    borderBottom: 'none',
    margin: '24px 0 16px 0',
  },
});

type DetailsBlacklist = keyof Resources.RequirementDetails;

const detailsBlackList: DetailsBlacklist[] = [
  'id',
  'equivalentRequirements',
  'mandatoryForSubmital',
  'mandatoryForFullPlacement',
  'mandatoryForTimesheetSubmission',
  'reference',
  'requiredTo',
  'priority',
  'otherProviderName',
  'description',
];

const CredentialDetails = ({
  subtitle,
  details,
  isDetailsVisible,
  isLoadingDetails,
  setDetailsVisibility,
}: CredentialDetailsProps) => {
  return (
    <DetailsModal
      bodyStyle={{
        padding: '0 40px 40px 40px',
        minHeight: 300,
      }}
      pageTitle={
        <ModalTitle
          subtitle={subtitle}
          title={
            <FormattedMessage
              id={messageIds.vms.matchRecord.credentials.detailsModal.pageTitle}
            />
          }
        />
      }
      title={
        !isLoadingDetails && (
          <H1
            padding={[0, 0, 0, 4]}
            inline
            gutter={4}
            gutterDirection="vertical"
          >
            <FormattedMessage
              id={messageIds.vms.matchRecord.credentials.detailsModal.title}
            />
          </H1>
        )
      }
      visible={isDetailsVisible}
      footer={null}
      onOk={() => {
        setDetailsVisibility(false);
      }}
      onCancel={() => setDetailsVisibility(false)}
    >
      {isLoadingDetails ? (
        <ModalSpinner spinning={isLoadingDetails} />
      ) : (
        Object.entries(details[0])
          .filter(
            values => !detailsBlackList.includes(values[0] as DetailsBlacklist),
          )
          .map(values => (
            <DynamicLabels
              key={values[0]}
              label={values[0]}
              value={values[1]}
            />
          ))
      )}
    </DetailsModal>
  );
};

export default CredentialDetails;
